import { useForm } from "@tanstack/react-form";
import { InputContainer, Button, InputText } from "@sellar-io/components";
import { TwoBusinessRegistrationCreateRequestType } from "@sellar-io/api-v2/types";

export type BusinessDetailsFormType = Omit<
  TwoBusinessRegistrationCreateRequestType["proposedBusiness"],
  "proprietorDetails"
> & {
  acceptTerms: boolean;
};

const fieldLabels: Record<string, string> = {
  businessType: "Business Type",
  countryCode: "Country",
  tradingName: "Trading Name",
  website: "Website (optional)",
  email: "Email",
  phone: "Phone",
  taxNumber: "Tax Number",
  street: "Street",
  building: "Building",
  apartment: "Apartment (optional)",
  postalCode: "Postal Code",
  city: "City",
  region: "Region",
  country: "Country",
};

const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
const phoneRegex = /^\+?\d{7,15}$/;
const urlRegex = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+[/#?]?.*$/;

type FormMeta = {
  submitAction: "back" | "submit" | null;
};

const defaultMeta: FormMeta = {
  submitAction: null,
};

export const BusinessDetailsForm = ({
  defaultValues,
  onSubmit,
  onBack,
  isLoading,
}: {
  defaultValues?: BusinessDetailsFormType;
  onSubmit: (data: BusinessDetailsFormType) => void;
  onBack: (data: BusinessDetailsFormType) => void;
  isLoading: boolean;
}) => {
  const form = useForm({
    defaultValues: defaultValues ?? {
      businessType: "SOLE_TRADER" as const,
      countryCode: "GB" as const,
      tradingName: "",
      website: "",
      email: "",
      phone: "",
      taxNumber: "",
      businessAddress: {
        apartment: "",
        building: "",
        street: "",
        postalCode: "",
        city: "",
        region: "",
        countryCode: "GB",
      },
      acceptTerms: false,
    },
    onSubmitMeta: defaultMeta,
    onSubmit: ({ value, meta }) => {
      if (meta.submitAction === "back") {
        onBack(value);
      }
      if (meta.submitAction === "submit") {
        onSubmit(value);
      }
    },
    onSubmitInvalid: (result) => {
      const errorFields = Object.keys(result.formApi.getAllErrors().fields);
      const firstInvalidField = errorFields[0];

      if (firstInvalidField) {
        const el = document.getElementsByName(firstInvalidField)[0];

        el?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        el?.focus();
      }
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="flex flex-col gap-8"
    >
      {/* Business Info */}
      <section className="space-y-4">
        <h2 className="border-b pb-2 text-xl font-semibold">
          4. Business Details
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {(
            [
              "businessType",
              "countryCode",
              "tradingName",
              "website",
              "email",
              "phone",
              "taxNumber",
            ] as const
          ).map((field) => (
            <form.Field
              key={field}
              name={field}
              validators={{
                onChange: ({ value }) => {
                  // Optional website
                  if (field === "website") {
                    if (!value) return undefined;
                    return urlRegex.test(value)
                      ? undefined
                      : "Enter a valid URL";
                  }

                  // Always valid (disabled fields)
                  if (["businessType", "countryCode"].includes(field)) {
                    return undefined;
                  }

                  // Required fields
                  if (!value || !value.toString().trim()) {
                    return `${fieldLabels[field]} is required`;
                  }

                  if (field === "email" && !emailRegex.test(value)) {
                    return "Enter a valid email";
                  }

                  if (field === "phone" && !phoneRegex.test(value)) {
                    return "Enter a valid phone number";
                  }

                  return undefined;
                },
              }}
            >
              {(state) => (
                <div className="flex flex-col gap-1">
                  <label className="font-medium">
                    {fieldLabels[field]}
                    {["tradingName", "email", "phone", "taxNumber"].includes(
                      field,
                    ) && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  <InputContainer>
                    <InputText
                      type={field === "website" ? "url" : "text"}
                      disabled={["countryCode", "businessType"].includes(field)}
                      defaultValue={state.state.value ?? ""}
                      placeholder={`Enter ${fieldLabels[field]}`}
                      onChange={(e) => state.handleChange(e.target.value)}
                      name={field}
                    />
                  </InputContainer>
                  {field === "email" && (
                    <p className="mt-1 text-sm text-gray-600">
                      <strong>Please note:</strong> This email address will be
                      used in all corresponse with relation to your business
                      registration and credit application with Two Inc
                    </p>
                  )}
                  {!state.state.meta.isValid && (
                    <span className="text-sm text-red-500">
                      {state.state.meta.errors.join(", ")}
                    </span>
                  )}
                </div>
              )}
            </form.Field>
          ))}
        </div>
      </section>

      {/* Business Address */}
      <section className="space-y-4">
        <h2 className="border-b pb-2 text-xl font-semibold">
          5. Business Address
        </h2>
        <div className="space-y-4 rounded border p-4 shadow-sm">
          {(
            [
              "apartment",
              "building",
              "street",
              "postalCode",
              "city",
              "region",
              "countryCode",
            ] as const
          ).map((subField) => (
            <form.Field
              key={subField}
              name={`businessAddress.${subField}`}
              validators={{
                onChange: ({ value }) => {
                  if (subField === "apartment" || subField === "countryCode")
                    return undefined;
                  if (!value || !value.toString().trim())
                    return `${fieldLabels[subField]} is required`;
                  return undefined;
                },
              }}
            >
              {(state) => (
                <div className="flex flex-col gap-1">
                  <label className="font-medium">
                    {fieldLabels[subField]}
                    {!["apartment", "countryCode"].includes(subField) && (
                      <span className="ml-1 text-red-500">*</span>
                    )}
                  </label>
                  <InputContainer>
                    <InputText
                      defaultValue={state.state.value ?? ""}
                      disabled={subField === "countryCode"}
                      placeholder={`Enter ${fieldLabels[subField]}`}
                      onChange={(e) => state.handleChange(e.target.value)}
                      name={`businessAddress.${subField}`}
                    />
                  </InputContainer>
                  {!state.state.meta.isValid && (
                    <span className="text-sm text-red-500">
                      {state.state.meta.errors.join(", ")}
                    </span>
                  )}
                </div>
              )}
            </form.Field>
          ))}
        </div>
      </section>

      {/* Acceptance */}
      <section className="rounded border bg-gray-50 p-4">
        <form.Field
          name="acceptTerms"
          validators={{
            onChange: ({ value }) =>
              !value ? "You must accept the terms" : undefined,
          }}
        >
          {(state) => (
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={state.state.value}
                onChange={(e) => state.handleChange(e.target.checked)}
                className="mt-1"
                name="acceptTerms"
              />
              <span>
                I confirm that the information provided is accurate. I
                understand that:
                <ul className="mt-2 list-disc space-y-1 pl-5 text-gray-600">
                  <li>
                    My business details will be reviewed as part of registration
                  </li>
                  <li>
                    A credit check will be carried out once registration is
                    complete
                  </li>
                  <li>
                    I will be notified of the outcome of my credit application
                  </li>
                </ul>
              </span>
              {!state.state.meta.isValid && (
                <span className="ml-2 text-sm text-red-500">
                  {state.state.meta.errors.join(", ")}
                </span>
              )}
            </label>
          )}
        </form.Field>
      </section>

      <form.Subscribe selector={(state) => state.values.acceptTerms}>
        {(accepted) => (
          <div className="flex flex-wrap items-center gap-2 pt-6">
            <Button
              type="button"
              size="large"
              color="secondary"
              onClick={() => form.handleSubmit({ submitAction: "back" })}
              disabled={isLoading}
            >
              Previous: Personal Details
            </Button>

            <Button
              type="submit"
              size="large"
              color="primary"
              disabled={!accepted || isLoading}
              onClick={() => form.handleSubmit({ submitAction: "submit" })}
            >
              Review & Submit
            </Button>
          </div>
        )}
      </form.Subscribe>
    </form>
  );
};
